import { notification } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { editTaskAction } from '../../../../redux/tasks/actions';
import { TaskButtons } from '../../atoms/buttons/TaskButtons';
import HtmlRenderer from '../../organisms/HtmlRenderer';
import TextEditorWrapper from '../editor/TextEditorWrapper';
import { checkContentForUploadingImage, prepareContentForSending } from '../editor/utils/editorContentOperations';

const EditableTaskDescription = ({ description, isModal, taskId, isGuest = false }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const textEditorRef = useRef({});
  const logTimer = useRef(null);
  const isSubmitting = useRef(false);

  const [isEditActive, setIsEditActive] = useState(false);
  const [updatedDescription, setUpdatedDescription] = useState(description);
  const [renderedDescription, setRenderedDescription] = useState(description);

  const { users } = useSelector((state) => state.userReducer);
  
  const mentionOptions = users?.map(user => ({
    id: user?._id,
    value: user?.fullName,
    icon: user?.avi
  }));

  const startEdit = useCallback((e) => {
    if (e?.target?.getAttribute('class') === 'ant-image-preview-wrap' || isGuest) {
      return;
    }
    setIsEditActive(true)
  }, [isGuest]);

  const cancelEdit = useCallback(() => {
    setUpdatedDescription(description);
    setIsEditActive(false);
  }, [description]);

  const handleTextEditorSubmit = useCallback((content, trimmedContent) => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    const descriptionToSet = prepareContentForSending(content);
    clearTimeout(logTimer.current);
    dispatch(
      editTaskAction(
        {
          _id: taskId,
          taskDescription: descriptionToSet,
        },
        null,
        () => {},
      ),
    );

    notification.success({
      placement: 'bottomRight',
      message: intl.formatMessage({
        id: 'TASK_DESCRIPTION_UPDATED',
        defaultMessage: 'Task Description Updated!',
      }),
    });

    setUpdatedDescription(descriptionToSet);
    setRenderedDescription(descriptionToSet);
    setIsEditActive(false);
    isSubmitting.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const submitEdit = useCallback(() => {
    textEditorRef?.current?.commands?.submit();
  }, []);

  const logUpdates = useCallback(() => {
    if (logTimer.current) {
      return;
    }
    console.info(`[EditableTaskDescription]: User is updating description of taskId: ${taskId}`);
    logTimer.current = setTimeout(() => {
      clearTimeout(logTimer.current);
      logTimer.current = null;
    }, 500);
  }, [taskId]);

  const handleTextEditorChange = useCallback((content) => {
    setUpdatedDescription(content);
    logUpdates();
  }, [logUpdates]);

  const isSendDisabled = useMemo(() => {
    const descriptionToSet = updatedDescription && prepareContentForSending(updatedDescription);
    return description === descriptionToSet || checkContentForUploadingImage(updatedDescription);
  }, [description, updatedDescription]);

  useEffect(() => {
    setRenderedDescription(description);
    setUpdatedDescription(description);
  }, [description]);

  useEffect(() => {
    // turn off edit mode when task changes
    setIsEditActive(false);
  }, [taskId])

  return isEditActive ? (
    <>
      <div
        style={{
          border: '1px solid #D6D6D6',
          padding: '0.75rem 1rem',
          textAlign: 'justify',
          borderRadius: '10px',
        }}
      >
        <div>
          <form className="h-full flex flex-col">
            <TextEditorWrapper
              entityId={taskId}
              entityType="task"
              large
              placeholder={intl.formatMessage({
                id: 'ADD_DESCRIPTION',
                defaultMessage: 'Add a description…',
              })}
              onChange={handleTextEditorChange}
              content={updatedDescription}
              initialContent={description}
              mentionOptions={mentionOptions}
              onSubmit={handleTextEditorSubmit}
              textEditorRef={textEditorRef}
              // handleBlur={submitEdit}
            />
          </form>
        </div>
      </div>
      <TaskButtons className="justify-end" disabled={isSendDisabled} onHandle={submitEdit} onCancel={cancelEdit} />
    </>
  ) : (
    <div
      onClick={startEdit}
      className={`task-descp-html-override w-full font-16 p-3 px-4 bg-gray-50 border-radius10 text-gray-600 hover:border-gray-300 border border-white ${isModal && 'min-h-40'}`}
    >
      {renderedDescription?.length > 0 ? (
        <HtmlRenderer html={renderedDescription} />
      ) : (
        <FormattedMessage id="ADD_DESCRIPTION_NEW" defaultMessage="Add description" />
      )}
    </div>
  );
};

export default EditableTaskDescription;
